// The photo-finish panel: shown with the official results when cats finished under half a second apart.
// The black-and-white finish-camera picture settles who was first; point at it to read any moment to the nanosecond.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { CAT_DISPLAY } from './cats';
import { drawFinishCamera, nsAtX, photoView } from './finishCamera';
import { ordinal } from './format';
import { formatMarginNs, formatNs, photoFinishes, verdict, type PhotoFinish as Photo } from './photoFinish';
import type { HudStore } from './store';

export function PhotoFinish({ store }: { store: HudStore }) {
  const snap = store.snapshot.value;
  const results = snap?.phase === 'finished' ? snap.results : undefined;
  const raceNo = snap?.raceNo ?? 0;
  const key = results ? `${raceNo}|${results.map((r) => `${r.id}:${r.seconds}:${r.dsq}`).join(',')}` : '';
  const photos = useMemo(() => (results ? photoFinishes(results, raceNo) : []), [key]);
  const [picked, setPicked] = useState({ key: '', index: 0 });
  const [hiddenKey, setHiddenKey] = useState('');
  if (!photos.length || hiddenKey === key) return null;
  const index = picked.key === key ? Math.min(picked.index, photos.length - 1) : 0;
  const photo = photos[index]!;
  return (
    <section class="panel photo-finish" aria-labelledby="photo-finish-title">
      <header>
        <h2 id="photo-finish-title">Photo finish</h2>
        {photos.length > 1 && (
          <div class="photo-picks" role="group" aria-label="Close finishes">
            {photos.map((p, i) => (
              <button type="button" key={i} aria-pressed={i === index} class={i === index ? 'on' : ''} onClick={() => setPicked({ key, index: i })}>
                {`${ordinal(p.cats[0]!.place)}–${ordinal(p.cats[p.cats.length - 1]!.place)}`}
              </button>
            ))}
          </div>
        )}
        <button type="button" class="close" aria-label="Hide the photo finish" onClick={() => setHiddenKey(key)}>×</button>
      </header>
      <FinishPhoto photo={photo} raceNo={raceNo} />
      <p class="verdict">{verdict(photo)}</p>
      <table>
        <thead><tr><th scope="col">Place</th><th scope="col">Cat</th><th scope="col">Crossed the line</th><th scope="col">Behind</th></tr></thead>
        <tbody>
          {photo.cats.map((c, i) => (
            <tr key={c.id}>
              <td>{c.place}</td>
              <th scope="row"><span class="swatch" style={{ background: CAT_DISPLAY[c.id].colour }} aria-hidden="true" />{CAT_DISPLAY[c.id].short}</th>
              <td class="num">{formatNs(c.ns)}</td>
              <td class="num">{i === 0 ? '' : `+${formatMarginNs(c.ns - photo.cats[i - 1]!.ns)}`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** The picture itself, drawn sharp at the screen's pixel density, with a cursor that reads the time anywhere on it. */
function FinishPhoto({ photo, raceNo }: { photo: Photo; raceNo: number }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 }); // CSS pixels; the stylesheet sets the shape
  const [cursor, setCursor] = useState<number | null>(null); // share of the width 0..1
  const view = photoView(photo);

  useEffect(() => {
    const el = canvas.current!;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const el = canvas.current!;
    if (!size.w || !size.h) return;
    const px = Math.max(2, window.devicePixelRatio || 1); // at least twice the CSS resolution: a sharp picture
    el.width = Math.round(size.w * px);
    el.height = Math.round(size.h * px);
    drawFinishCamera(el.getContext('2d', { willReadFrequently: true })!, el.width, el.height, px, photo, raceNo);
  }, [photo, raceNo, size.w, size.h]);
  useEffect(() => setCursor(null), [photo]);

  const at = (e: PointerEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setCursor(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)));
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const step = (e.shiftKey ? 0.02 : 0.002) * (e.key === 'ArrowLeft' ? -1 : 1);
    setCursor((c) => Math.min(1, Math.max(0, (c ?? 0.5) + step)));
  };
  const description = `Black-and-white finish camera picture. ${photo.cats.map((c) => `${CAT_DISPLAY[c.id].name} crosses at ${formatNs(c.ns)}`).join('. ')}. Point at the picture or use the arrow keys to read the time at any spot.`;
  return (
    <div class="photo" tabIndex={0} role="img" aria-label={description} onPointerMove={at} onPointerDown={at} onPointerLeave={() => setCursor(null)} onKeyDown={onKey} onBlur={() => setCursor(null)}>
      <canvas ref={canvas} />
      {cursor !== null && (
        <div class={`cursor${cursor > 0.6 ? ' flip' : ''}`} style={{ left: `${cursor * 100}%` }} aria-hidden="true">
          <span>{formatNs(nsAtX(view, cursor, 1))}</span>
        </div>
      )}
    </div>
  );
}
